<template>
  <div class="admin-page">
        <div class="admin-toolbar">
      <div class="admin-toolbar__actions">
            <el-select
              v-model="visibleFilter"
              placeholder="展示状态"
              class="filter-select"
              @change="handleFilterChange"
            >
              <el-option label="全部" :value="-1" />
              <el-option label="展示中" :value="1" />
              <el-option label="已隐藏" :value="0" />
            </el-select>
            <el-button type="primary" @click="openCreateDialog">新增动态</el-button>
            <el-button @click="fetchList">刷新</el-button>
          </div>
        </div>

        <el-table :data="items" v-loading="loading" style="width: 100%">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column label="封面" width="120">
            <template #default="scope">
              <el-image
                v-if="scope.row.imageUrl"
                :src="scope.row.imageUrl"
                fit="cover"
                style="width: 96px; height: 54px; border-radius: 4px"
              />
              <span v-else class="no-cover">无</span>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="标题" min-width="160" show-overflow-tooltip />
          <el-table-column prop="excerpt" label="摘要" min-width="200" show-overflow-tooltip />
          <el-table-column prop="sortOrder" label="排序" width="80" />
          <el-table-column prop="isVisible" label="展示" width="90">
            <template #default="scope">
              <el-tag :type="scope.row.isVisible === 1 ? 'success' : 'info'">
                {{ scope.row.isVisible === 1 ? '展示' : '隐藏' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="240" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="previewItem(scope.row)">预览</el-button>
              <el-button size="small" @click="openEditDialog(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="deleteItem(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <AdminPagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑动态' : '新增动态'"
      width="92%"
      top="3vh"
      class="content-edit-dialog"
      destroy-on-close
      @closed="imageBroken = false"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="88px">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="动态标题" />
        </el-form-item>
        <el-form-item label="摘要" prop="excerpt">
          <el-input v-model="form.excerpt" type="textarea" :rows="2" placeholder="列表页显示的摘要" />
        </el-form-item>
        <el-form-item label="封面图" prop="imageUrl">
          <div class="cover-upload-wrap">
            <el-upload
              class="cover-uploader"
              action="/api/v1/upload"
              :show-file-list="false"
              :headers="uploadHeaders"
              :data="uploadData"
              accept="image/png,image/jpeg,image/webp"
              :before-upload="beforeImageUpload"
              :on-success="handleImageSuccess"
            >
              <img
                v-if="form.imageUrl && !imageBroken"
                :src="form.imageUrl"
                class="cover-preview"
                alt="封面"
                @error="imageBroken = true"
              />
              <div v-else class="cover-placeholder">
                <el-icon class="cover-upload-icon"><Plus /></el-icon>
                <span>上传封面（可选）</span>
              </div>
            </el-upload>
            <el-input v-model="form.imageUrl" placeholder="上传后自动填入或粘贴图片地址" />
          </div>
        </el-form-item>
        <el-form-item label="正文" prop="content" class="content-editor-item">
          <MonoEdit v-model="form.content" height="min(65vh, 680px)" />
          <p class="form-hint block-hint">正文支持 Markdown 语法，详情页可渲染标题、列表、代码块与表格。</p>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" :max="9999" />
          <span class="form-hint">数字越小越靠前</span>
        </el-form-item>
        <el-form-item label="是否展示" prop="isVisible">
          <el-switch
            v-model="form.isVisible"
            :active-value="1"
            :inactive-value="0"
            active-text="展示"
            inactive-text="隐藏"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import AdminPagination from '@/components/AdminPagination.vue'
import MonoEdit from '@/components/MonoEdit.vue'
import axios from 'axios'
import { validatePhotoUpload, adminUploadFormData, uploadResponseToUrl } from '@/utils/photoUpload'

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
  'Content-Type': 'application/json'
})

const emptyForm = () => ({
  id: 0,
  title: '',
  excerpt: '',
  content: '',
  imageUrl: '',
  sortOrder: 0,
  isVisible: 1
})

export default {
  name: 'HomeNewsManagement',
  components: { AdminPagination, MonoEdit, Plus },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const saving = ref(false)
    const items = ref([])
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const visibleFilter = ref(-1)
    const dialogVisible = ref(false)
    const isEdit = ref(false)
    const formRef = ref(null)
    const form = reactive(emptyForm())
    const imageBroken = ref(false)

    const uploadHeaders = computed(() => ({
      Authorization: `Bearer ${localStorage.getItem('jwt_token') || ''}`
    }))
    const uploadData = computed(() => adminUploadFormData('news'))

    const rules = {
      title: [{ required: true, message: '请输入标题', trigger: 'blur' }],
      content: [{ required: true, message: '请输入正文', trigger: 'blur' }]
    }

    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    const fetchList = async () => {
      if (!checkLogin()) return
      loading.value = true
      try {
        const response = await axios.post(
          '/api/v1/admin/home/news/list',
          {
            page: currentPage.value,
            pageSize: pageSize.value,
            isVisible: visibleFilter.value
          },
          { headers: authHeaders() }
        )
        if (response.data.code === 200 && response.data.data) {
          items.value = response.data.data.items || []
          total.value = response.data.data.total || 0
        } else {
          ElMessage.error(response.data.message || '获取列表失败')
        }
      } catch (error) {
        console.error(error)
        ElMessage.error('获取列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleFilterChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const handleSizeChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const handlePageChange = () => {
      fetchList()
    }

    const resetForm = () => {
      Object.assign(form, emptyForm())
    }

    const openCreateDialog = () => {
      isEdit.value = false
      resetForm()
      imageBroken.value = false
      dialogVisible.value = true
    }

    const openEditDialog = (row) => {
      isEdit.value = true
      imageBroken.value = false
      Object.assign(form, {
        id: row.id,
        title: row.title,
        excerpt: row.excerpt,
        content: row.content || '',
        imageUrl: row.imageUrl,
        sortOrder: row.sortOrder,
        isVisible: row.isVisible
      })
      dialogVisible.value = true
    }

    const beforeImageUpload = (file) => validatePhotoUpload(file, (msg) => ElMessage.error(msg))

    const handleImageSuccess = (response) => {
      if (response.code === 200) {
        form.imageUrl = uploadResponseToUrl(response)
        imageBroken.value = false
        ElMessage.success('封面上传成功')
      } else {
        ElMessage.error(response.message || '上传失败')
      }
    }

    const submitForm = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
      } catch {
        return
      }

      saving.value = true
      try {
        const url = isEdit.value ? '/api/v1/home/news/update' : '/api/v1/home/news/create'
        const payload = isEdit.value
          ? { ...form }
          : {
              title: form.title,
              excerpt: form.excerpt,
              content: form.content,
              imageUrl: form.imageUrl,
              sortOrder: form.sortOrder,
              isVisible: form.isVisible
            }

        const response = await axios.post(url, payload, { headers: authHeaders() })
        if (response.data.code === 200) {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          fetchList()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error(error)
        ElMessage.error(error.response?.data?.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    const deleteItem = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除「${row.title}」吗？`, '删除确认', { type: 'warning' })
      } catch {
        return
      }
      try {
        const response = await axios.post(
          '/api/v1/home/news/delete',
          { id: row.id },
          { headers: authHeaders() }
        )
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          fetchList()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch {
        ElMessage.error('删除失败')
      }
    }

    const previewItem = (row) => {
      window.open(`/home/news/${row.id}`, '_blank')
    }

    onMounted(fetchList)

    return {
      loading,
      saving,
      items,
      currentPage,
      pageSize,
      total,
      visibleFilter,
      dialogVisible,
      isEdit,
      formRef,
      form,
      imageBroken,
      uploadHeaders,
      uploadData,
      rules,
      fetchList,
      handleFilterChange,
      handleSizeChange,
      handlePageChange,
      openCreateDialog,
      openEditDialog,
      beforeImageUpload,
      handleImageSuccess,
      submitForm,
      deleteItem,
      previewItem
    }
  }
}
</script>

<style scoped>

.home-news-management {
  min-height: 100%;
}
.no-cover {
  color: #909399;
  font-size: 12px;
}
.form-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
.block-hint {
  display: block;
  margin: 8px 0 0;
  margin-left: 0;
  line-height: 1.5;
}
.cover-upload-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}
.cover-uploader {
  width: 160px;
  height: 90px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
}
.cover-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.cover-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #909399;
}
.cover-upload-icon {
  font-size: 24px;
}

:deep(.content-edit-dialog .el-dialog__body) {
  max-height: calc(94vh - 120px);
  overflow-y: auto;
}

.content-editor-item :deep(.el-form-item__content) {
  line-height: normal;
}

</style>
